<template>
    <div ref="containerRef" class="relative w-full h-full overflow-hidden">
        <!-- Poster image: always behind, and the LCP element for hero usage.
             Rendered eagerly (not lazily injected) so it is in the initial DOM
             and fetched straight away; no opacity fade so it paints — and counts
             as LCP — immediately. `priority` raises its fetch priority. -->
        <img
            :src="poster"
            :alt="alt"
            class="absolute inset-0 w-full h-full object-cover"
            :fetchpriority="priority ? 'high' : 'auto'"
            :loading="priority ? 'eager' : 'lazy'"
            decoding="async"
        />

        <!-- Native video (mp4) -->
        <video
            v-if="canEmbed && mode === 'native' && src"
            ref="videoRef"
            class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full object-cover transition-opacity"
            :class="ready ? 'opacity-100' : 'opacity-0'"
            :style="{ transitionDuration: `${duration}ms` }"
            :autoplay="autoplay"
            :loop="loop"
            :muted="muted"
            playsinline
            @playing="onReady"
        >
            <source :src="src" :type="type" />
        </video>

        <!-- YouTube / Vimeo iframe. Sized to cover the container (not the
             viewport) so the embed scales correctly whether this component is a
             full-screen hero or a small card. iframeSize is recomputed on
             resize; the transform just keeps the overflow centred. -->
        <iframe
            v-if="canEmbed && mode !== 'native' && embedUrl"
            :src="embedUrl"
            :title="alt || 'Video'"
            class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 border-0 transition-opacity pointer-events-none"
            :class="ready ? 'opacity-100' : 'opacity-0'"
            :style="{
                transitionDuration: `${duration}ms`,
                width: `${iframeSize.width}px`,
                height: `${iframeSize.height}px`,
            }"
            allow="autoplay; fullscreen"
            allowfullscreen
            @load="onReady"
        ></iframe>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

defineOptions({ inheritAttrs: false });

type VideoMode = 'native' | 'youtube' | 'vimeo';

const props = withDefaults(defineProps<{
    poster: string;
    src?: string;
    youtube_id?: string;
    vimeo_id?: string;
    type?: string;
    alt?: string;
    autoplay?: boolean;
    loop?: boolean;
    muted?: boolean;
    duration?: number;
    // Set on above-the-fold (hero) usage: marks the poster as the LCP image so
    // it is fetched with high priority.
    priority?: boolean;
}>(), {
    type: 'video/mp4',
    alt: '',
    autoplay: true,
    loop: true,
    muted: true,
    duration: 800,
    priority: false,
});

const ready = ref(false);
// Gate for the heavy video/iframe — only set once the element is near the
// viewport AND the browser is idle, so the embed never competes with the
// poster (LCP) for bandwidth or main-thread time.
const canEmbed = ref(false);
const containerRef = ref<HTMLElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);
// Pixel dimensions for the embed iframe, recomputed to cover the container at a
// 16:9 aspect ratio. Seeded non-zero so the first paint isn't collapsed.
const iframeSize = ref({ width: 0, height: 0 });
const ASPECT = 16 / 9;
let observer: IntersectionObserver | null = null;
let resizeObserver: ResizeObserver | null = null;

// Size the 16:9 iframe so it fully covers the container box, mirroring what
// `object-fit: cover` does for the native <video>.
function computeIframeSize() {
    const el = containerRef.value;
    if (!el) return;
    const { clientWidth: w, clientHeight: h } = el;
    if (!w || !h) return;
    if (w / h > ASPECT) {
        iframeSize.value = { width: w, height: w / ASPECT };
    } else {
        iframeSize.value = { width: h * ASPECT, height: h };
    }
}

const mode = computed<VideoMode>(() => {
    if (props.src) return 'native';
    if (props.youtube_id) return 'youtube';
    if (props.vimeo_id) return 'vimeo';
    return 'native';
});

const embedUrl = computed(() => {
    if (props.youtube_id) {
        const params = new URLSearchParams({
            autoplay: props.autoplay ? '1' : '0',
            mute: props.muted ? '1' : '0',
            loop: props.loop ? '1' : '0',
            playlist: props.youtube_id,
            controls: '0',
            showinfo: '0',
            rel: '0',
            modestbranding: '1',
            playsinline: '1',
        });
        return `https://www.youtube.com/embed/${props.youtube_id}?${params}`;
    }
    if (props.vimeo_id) {
        const params = new URLSearchParams({
            autoplay: props.autoplay ? '1' : '0',
            muted: props.muted ? '1' : '0',
            loop: props.loop ? '1' : '0',
            background: '1',
            playsinline: '1',
        });
        return `https://player.vimeo.com/video/${props.vimeo_id}?${params}`;
    }
    return '';
});

function onReady() {
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            ready.value = true;
        });
    });
}

// Once the element is near the viewport, wait for an idle slot before mounting
// the embed so the poster gets to paint first.
function scheduleEmbed() {
    if (canEmbed.value) return;
    const run = () => {
        canEmbed.value = true;
        // Size the iframe before it paints, then keep it in sync with the box.
        computeIframeSize();
        if (containerRef.value && 'ResizeObserver' in window) {
            resizeObserver = new ResizeObserver(() => computeIframeSize());
            resizeObserver.observe(containerRef.value);
        }
    };
    if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(run, { timeout: 2000 });
    } else {
        setTimeout(run, 300);
    }
}

onMounted(() => {
    if (containerRef.value) {
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        scheduleEmbed();
                        observer?.disconnect();
                    }
                });
            },
            { rootMargin: '200px' }
        );
        observer.observe(containerRef.value);
    }
});

onUnmounted(() => {
    observer?.disconnect();
    resizeObserver?.disconnect();
});
</script>
